/**
 * WizardStepDetails Component
 *
 * Step 2 of the presentation wizard: Presentation details form.
 * Auto-populates with user's default evaluation framework.
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Alert,
    CircularProgress,
    Chip,
    FormControlLabel,
    Switch,
    Grid,
    SelectChangeEvent
} from '@mui/material';
import { useUser } from '../../../contexts/UserContext';
import { useTenantSettings } from '../../../hooks/useTenantSettings';
import { getConsultants } from '../../../utils/api/consultantApi';
import { getEvaluationSchemas } from '../../../utils/api/evaluationSchemaApi';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebaseClient';
import { WizardFormData } from './PresentationWizard';

interface WizardStepDetailsProps {
    formData: WizardFormData;
    onFormDataChange: (data: WizardFormData) => void;
    onBack: () => void;
    onNext: () => void;
}

const WizardStepDetails: React.FC<WizardStepDetailsProps> = ({ formData, onFormDataChange, onBack, onNext }) => {
    const { user, userProfile, isTenantAdmin, isGlobalAdmin } = useUser();
    const { tenantSettings } = useTenantSettings();

    const [consultants, setConsultants] = useState<any[]>([]);
    const [loadingConsultants, setLoadingConsultants] = useState(false);
    const [evaluationSchemas, setEvaluationSchemas] = useState<any[]>([]);
    const [loadingSchemas, setLoadingSchemas] = useState(false);
    const [useCustomSchema, setUseCustomSchema] = useState(false);
    const [hasUserDefaultSchema, setHasUserDefaultSchema] = useState(false);
    const [checkingUserSchema, setCheckingUserSchema] = useState(false);
    const [error, setError] = useState('');

    // Determine if coach mode is enabled
    const isCoachModeEnabled = tenantSettings?.coachMode === true;
    const canSelectConsultant = isCoachModeEnabled;
    const shouldAutoSelectCurrentUser = !canSelectConsultant;

    // Auto-populate consultant field with current user
    useEffect(() => {
        if (shouldAutoSelectCurrentUser && user && !formData.consultantId) {
            const userName = user.displayName || (userProfile?.firstName && userProfile?.lastName
                ? `${userProfile.firstName} ${userProfile.lastName}`
                : user.email) || 'Current User';

            onFormDataChange({
                ...formData,
                consultantId: user.uid,
                consultantName: userName,
                presenterName: userName
            });
        }
    }, [shouldAutoSelectCurrentUser, user, userProfile, formData, onFormDataChange]);

    // Check if user has default schema
    useEffect(() => {
        if (user?.uid) {
            checkUserDefaultSchema();
        }
    }, [user]);

    // Load consultants if needed
    useEffect(() => {
        console.log('[DEBUG] Checking if can load consultants:', { canSelectConsultant, isCoachModeEnabled });
        if (canSelectConsultant) {
            loadConsultantsList();
        }
    }, [canSelectConsultant, isCoachModeEnabled]);

    // Load evaluation schemas if custom schema is enabled
    useEffect(() => {
        if (useCustomSchema) {
            loadEvaluationSchemasList();
        }
    }, [useCustomSchema]);

    const checkUserDefaultSchema = async () => {
        try {
            if (!user?.uid) return;
            setCheckingUserSchema(true);
            const userDoc = await getDoc(doc(db, 'users', user.uid));

            if (userDoc.exists()) {
                const userData = userDoc.data();
                const hasDefault = !!userData.defaultEvaluationSchemaId;
                setHasUserDefaultSchema(hasDefault);

                if (!hasDefault) {
                    console.warn('User does not have a default evaluation schema assigned');
                }
            } else {
                setHasUserDefaultSchema(false);
            }
        } catch (err) {
            console.error('Error checking user default schema:', err);
            setHasUserDefaultSchema(false);
        } finally {
            setCheckingUserSchema(false);
        }
    };

    const loadConsultantsList = async () => {
        try {
            console.log('[DEBUG] Loading consultants list...');
            setLoadingConsultants(true);
            const result = await getConsultants({ active: true }) as { success: boolean; consultants?: any[]; error?: string };
            console.log('[DEBUG] Consultants result:', result);

            if (result.success && Array.isArray(result.consultants)) {
                console.log(`[DEBUG] Found ${result.consultants.length} consultants`);
                setConsultants(result.consultants);
            } else {
                console.warn('[DEBUG] No consultants found or invalid format');
                setConsultants([]);
            }
        } catch (err) {
            console.error('[DEBUG] Error loading consultants:', err);
            setConsultants([]);
        } finally {
            setLoadingConsultants(false);
        }
    };

    const loadEvaluationSchemasList = async () => {
        try {
            setLoadingSchemas(true);
            const result = await getEvaluationSchemas({
                includeGlobal: true
            }) as { success: boolean; schemas?: any[]; error?: string };

            if (result.success && Array.isArray(result.schemas)) {
                setEvaluationSchemas(result.schemas);
            } else {
                setEvaluationSchemas([]);
            }
        } catch (err) {
            console.error('Error loading evaluation schemas:', err);
            setEvaluationSchemas([]);
        } finally {
            setLoadingSchemas(false);
        }
    };

    const handleChange = (field: keyof WizardFormData) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
        onFormDataChange({
            ...formData,
            [field]: event.target.value
        });
    };

    const handleNext = () => {
        setError('');

        // Validation
        if (!formData.description || formData.description.trim() === '') {
            setError('Please provide a description for your presentation');
            return;
        }

        if (!hasUserDefaultSchema && !useCustomSchema) {
            setError('No default evaluation framework found. Please enable "Use custom evaluation framework" and select one to proceed.');
            return;
        }

        if (useCustomSchema && !formData.evaluationSchemaId) {
            setError('Please select an evaluation framework');
            return;
        }

        onNext();
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Typography variant="h6" gutterBottom>
                Presentation Details
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
                Provide information about your presentation. Your default evaluation framework will be used automatically.
            </Typography>

            {/* Framework Status Check */}
            {checkingUserSchema ? (
                <Alert severity="info" icon={<CircularProgress size={20} />} sx={{ mb: 3 }}>
                    Checking evaluation framework...
                </Alert>
            ) : !hasUserDefaultSchema && !useCustomSchema ? (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    No default evaluation framework assigned. Please select a custom evaluation framework below to proceed.
                </Alert>
            ) : hasUserDefaultSchema && !useCustomSchema ? (
                <Alert severity="success" sx={{ mb: 3 }}>
                    Using your default evaluation framework
                </Alert>
            ) : null}

            {/* Form Fields */}
            <Box component="form" sx={{ '& > :not(style)': { mb: 2.5 } }}>
                {/* Description - Single line */}
                <TextField
                    fullWidth
                    label="Presentation Description *"
                    placeholder="e.g., Q4 Product Demo for Enterprise Client"
                    value={formData.description || ''}
                    onChange={handleChange('description')}
                    helperText="Brief description of your presentation"
                />

                {/* Product Name */}
                <TextField
                    fullWidth
                    label="Product Name"
                    placeholder="e.g., EvalPro Platform"
                    value={formData.productName || ''}
                    onChange={handleChange('productName')}
                    helperText="Name of the product being presented (optional)"
                />

                {/* Presentation Type and Language in same row */}
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth>
                            <InputLabel>Presentation Type</InputLabel>
                            <Select
                                value={formData.presentationType || ''}
                                onChange={handleChange('presentationType')}
                                label="Presentation Type"
                            >
                                <MenuItem value="">
                                    <em>Select type</em>
                                </MenuItem>
                                <MenuItem value="Sales Demo">Sales Demo</MenuItem>
                                <MenuItem value="Product Presentation">Product Presentation</MenuItem>
                                <MenuItem value="Training Session">Training Session</MenuItem>
                                <MenuItem value="Webinar">Webinar</MenuItem>
                                <MenuItem value="Internal Presentation">Internal Presentation</MenuItem>
                                <MenuItem value="Client Meeting">Client Meeting</MenuItem>
                                <MenuItem value="Other">Other</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth>
                            <InputLabel>Language</InputLabel>
                            <Select
                                value={formData.language || 'en'}
                                onChange={handleChange('language')}
                                label="Language"
                            >
                                <MenuItem value="en">English</MenuItem>
                                <MenuItem value="es">Spanish</MenuItem>
                                <MenuItem value="fr">French</MenuItem>
                                <MenuItem value="de">German</MenuItem>
                                <MenuItem value="pt">Portuguese</MenuItem>
                                <MenuItem value="it">Italian</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                {/* Consultant Selection (if coach mode enabled) */}
                {canSelectConsultant && (
                    <FormControl fullWidth>
                        <InputLabel>Consultant</InputLabel>
                        <Select
                            value={formData.consultantId || ''}
                            onChange={(e) => {
                                const newId = e.target.value;
                                const consultant = consultants.find(c => c.id === newId);
                                const consultantName = consultant
                                    ? (consultant.fullName || consultant.name || `${consultant.firstName || ''} ${consultant.lastName || ''}`.trim())
                                    : '';

                                onFormDataChange({
                                    ...formData,
                                    consultantId: newId,
                                    consultantName: consultantName,
                                    presenterName: consultantName
                                });
                            }}
                            label="Consultant"
                            disabled={loadingConsultants}
                        >
                            <MenuItem value="">
                                <em>Select consultant</em>
                            </MenuItem>
                            {consultants.map((consultant) => (
                                <MenuItem key={consultant.id} value={consultant.id}>
                                    {consultant.fullName || consultant.name || `${consultant.firstName || ''} ${consultant.lastName || ''}`.trim() || 'Unnamed Consultant'}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {/* Custom Evaluation Framework Toggle (available for all users) */}
                {(
                    <>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={useCustomSchema}
                                    onChange={(e) => {
                                        const newValue = e.target.checked;
                                        setUseCustomSchema(newValue);
                                        // Clear evaluationSchemaId when toggle is turned off
                                        if (!newValue) {
                                            onFormDataChange({
                                                ...formData,
                                                evaluationSchemaId: undefined
                                            });
                                        }
                                    }}
                                />
                            }
                            label="Use custom evaluation framework (override default)"
                        />

                        {useCustomSchema && (
                            <FormControl fullWidth>
                                <InputLabel>Evaluation Framework</InputLabel>
                                <Select
                                    value={formData.evaluationSchemaId || ''}
                                    onChange={handleChange('evaluationSchemaId')}
                                    label="Evaluation Framework"
                                    disabled={loadingSchemas}
                                >
                                    <MenuItem value="">
                                        <em>Select framework</em>
                                    </MenuItem>
                                    {evaluationSchemas.map((schema) => (
                                        <MenuItem key={schema.id} value={schema.id}>
                                            {schema.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </>
                )}
            </Box>

            {/* Error Message */}
            {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Navigation Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button
                    variant="outlined"
                    onClick={onBack}
                    size="large"
                >
                    Back
                </Button>
                <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={checkingUserSchema}
                    size="large"
                >
                    Next: Start Evaluation
                </Button>
            </Box>
        </Box>
    );
};

export default WizardStepDetails;
